import React, { useCallback, useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  HeaderTemplate,
  SectionHeader,
  Stepper,
  TitleHeader,
  Typography,
} from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';


import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory, useParams, Prompt } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { ApplicationType } from '../../../api/candidate/config/model';
import UploadDocuments from '../../accessArrangement/SelectCondition/components/UploadDocuments';
import RouteKey from '../../../routes/key';
import {
  usePostUploadPresignedUrl
} from '../../../api-refactored/special-consideration/usePostUploadPresignedUrl';
import { PresignedUrlResponse } from '../../../models/PresignedUrl';
import {
  UploadedFileModel
} from '../../../api-refactored/special-consideration/useGetUploadedFileInfo';
import { prepareFiles } from '../../../utils/UploadUtil';
import DraftTimeStamp from '../../../components/DraftTimeStamp';
import {
  useGetRequestSummary
} from '../../../api-refactored/group-special-consideration/useGetRequestSummary';
import DraftMessage from '../../../modules/specialConsideration/components/DraftMessage';
import DocumentBadge from '../../../components/DocumentBadge';
import {
  useGetApplicationFilesConfig
} from '../../../api/document/attachedFiles/queries-services/queries';

const {
  selectSubject,
  amendSelectSubject,
  reviewApplication,
  amendReviewApplication,
  coordinatorUploadRankingDetails,
  amendCoordinatorUploadRankingDetails,
  coordinatorApplicationReview,
  amendCoordinatorApplicationReview,
} = RouteKey['special-consideration'].child;

interface UploadSupportingDocumentsContentProps {
  routeSteps: { label: string }[];
  uploadedFileInfo?: UploadedFileModel[];
  files: UploadedFile[];
  draftFiles: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  isGroup: boolean;
  amendment: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    left: '-10%',
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-8%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-2%',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(8),
  },
  sectionText: {
    marginBottom: theme.spacing(4),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  submittedDocuments: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
              marginBottom: theme.spacing(1),
              color: theme.color.text.textHeader,
            },
  documentBadgeContainer: {
    marginBottom: theme.spacing(4),
  },
  keyLabel: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
}));

const goBackClick = (
  isGroup: boolean,
  amendment: boolean,
  history: RouteComponentProps['history']
) => {
  if (isGroup && amendment) {
    history.push(`${amendCoordinatorUploadRankingDetails.route}`);
  }
  if (isGroup && !amendment) {
    history.push(`${coordinatorUploadRankingDetails.route}`);
  }
  if (!isGroup && amendment) {
    history.push(`${amendSelectSubject.route}`);
  }
  if (!isGroup && !amendment) {
    history.push(`${selectSubject.route}`);
  }
};

const goToNextPageFunction = (
  isGroup: boolean,
  amendment: boolean,
  history: RouteComponentProps['history']
) => {
  if (isGroup && amendment) {
    history.push(`${amendCoordinatorApplicationReview.route}`);
  }
  if (isGroup && !amendment) {
    history.push(`${coordinatorApplicationReview.route}`);
  }
  if (!isGroup && amendment) {
    history.push(`${amendReviewApplication.route}`);
  }
  if (!isGroup && !amendment) {
    history.push(`${reviewApplication.route}`);
  }
};

const UploadSupportingDocumentsContent = ({
  routeSteps,
  uploadedFileInfo,
  files,
  draftFiles,
  setFiles,
  setDraftFiles,
  isGroup,
  amendment,
}: UploadSupportingDocumentsContentProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory<{ isPSLE: string }>();
  const { uuid } = useParams<{ uuid: string }>();

  const [error, setError] = useState<boolean>(false);

  const [allowProceed, setAllowProceed] = useState(false);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [errorMsg, setErrorMsg] = useState<string>('');

  const { data: applicationData } = useGetRequestSummary({
    uuid,
    enabled: isGroup,
  });

  const isPSLE = isGroup && applicationData ? applicationData.examLevel === 'PSLE' : false;

  const getUploadPresignedUrlMutation = usePostUploadPresignedUrl({
    onSuccess: (res: PresignedUrlResponse[]) => {
      const newFilesInfo: React.SetStateAction<UploadedFile[]> = [];
      files.forEach((_item: UploadedFile, index: number) => {
        const pendingFile = files[index];
        const fileData = res[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        newFilesInfo.push(pendingFile);
      });
      setFiles(newFilesInfo);

      setShouldSubmit(true);
    },
    onError: (err) => {
      setApiButtonStatus('error');
      setErrorMsg(err?.response?.data.message!);
      setFiles(files.map((file) => ({ ...file, pending: false })));
    },
  });

  const goToNextPage = useCallback(() => goToNextPageFunction(isGroup, amendment, history), [
    isGroup,
    amendment,
    history,
  ]);

  useEffect(() => {
    const isUploading = files.some((file) => file.pending);
    const hasError = files.some((file) => file.hasError);
    switch (true) {
      case isUploading:
        setApiButtonStatus('loading');
        break;
      case hasError:
        setShouldSubmit(false);
        setApiButtonStatus('idle');
        break;
      case files.length > 0 && !isUploading && shouldSubmit:
        goToNextPage();
        break;
      default:
        setApiButtonStatus('idle');
        break;
    }
  }, [amendment, files, goToNextPage, shouldSubmit]);

  useEffect(() => {
    if (draftFiles.length > 0 || files.length > 0) {
      setError(false);
    }
  }, [files, draftFiles, uploadedFileInfo]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const uploadFileClickHandler = () => {
    if (draftFiles.length < 1 && files.length < 1) {
      setError(true);
    }
    if (!error && files && files.length > 0) {
      const { filenames } = prepareFiles(files, setFiles);
      getUploadPresignedUrlMutation.mutate({
        uuid,
        applicationType: ApplicationType.SPECIAL_CONSIDERATION,
        filenames,
      });
    }
    if (!error && draftFiles && draftFiles.length > 0 && files.length === 0) {
      goToNextPage();
    }
  };

  const submittedFilesLength = uploadedFileInfo?.filter(
    (item: UploadedFileModel) => item.isSubmitted
  ).length;

  const { data: applicationFilesConfig } = useGetApplicationFilesConfig(
    ApplicationType.SPECIAL_CONSIDERATION
  );

  return (
    <>
    <Prompt
      when={!allowProceed}
      message={(location) => {
        const nextPath = location.pathname;

        if (
          nextPath.includes(reviewApplication.route) ||
          nextPath.includes(amendReviewApplication.route)
        ) {
          return true; // allow navigation without popup
        }
          return t("specialConsideration->>ApplyForSpecialConsideration.ReminderToSubmitPopUp");
      }}
    />
      <TitleHeader title={t('specialConsideration->>Instructions.ApplyForSpecialConsideration')} />
      <HeaderTemplate>
        <Stepper className={classes.stepper} activeStep={1} steps={routeSteps} />
      </HeaderTemplate>
      <ContentTemplate>
        <SectionHeader
          title={t(
            'specialConsideration->>ApplyForSpecialConsideration.Upload Supporting Document(s)'
          )}
        />
         <Typography className={classes.inputLabel} variant="body1">
           {t('specialConsideration->>ApplyForSpecialConsideration.uploadInstruction')}
         </Typography>

        {amendment && (
          <>
            <Typography variant="h5" className={classes.keyLabel}>
              {t('specialConsideration->>ApplyForSpecialConsideration.SubmittedDocuments')}
            </Typography>
            {uploadedFileInfo &&
              uploadedFileInfo
                .filter((item: UploadedFileModel) => item.isSubmitted)
                .map((item: UploadedFileModel) => (
                  <DocumentBadge
                    className={classes.documentBadgeContainer}
                    documentName={item.fileName}
                    key={item.fileUuid}
                  />
                ))}
          </>
        )}

        <Typography className={classes.sectionText} variant="body1">
          {`${t(
            'specialConsideration->>ApplyForSpecialConsideration.UploadDocumentDescription1'
          )}\n${t(
            'specialConsideration->>ApplyForSpecialConsideration.UploadDocumentDescription2'
          )}\n`}
          {isPSLE ? (
            t('specialConsideration->>ApplyForSpecialConsideration.UploadDocumentDescriptionPSLE')
          ) : (
            <ul>
              <li>
                <Typography variant="body1">
                  {t('specialConsideration->>ApplyForSpecialConsideration.UploadDocumentBullet1')}
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  {t('specialConsideration->>ApplyForSpecialConsideration.UploadDocumentBullet2')}
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  {t('specialConsideration->>ApplyForSpecialConsideration.UploadDocumentBullet3')}
                </Typography>
              </li>
            </ul>
          )}
        </Typography>
        {applicationFilesConfig && submittedFilesLength !== undefined && (
          <>
            <UploadDocuments
              files={files}
              setFiles={setFiles}
              hasError={error}
              submittedFiles={submittedFilesLength}
              applicationType={ApplicationType.SPECIAL_CONSIDERATION}
              draftFiles={draftFiles}
              setDraftFiles={setDraftFiles}
            />
            {error && submittedFilesLength >= applicationFilesConfig.fileNumLimit && (
              <Typography variant="body2" className={classes.errString}>
                {t('specialConsideration->>ApplyForSpecialConsideration.FilesExceededError')}
              </Typography>
            )}
            {error && submittedFilesLength < applicationFilesConfig.fileNumLimit && (
              <Typography variant="body2" className={classes.errString}>
                {t('specialConsideration->>ApplyForSpecialConsideration.DocumentUploadError')}
              </Typography>
            )}
          </>
        )}

        <ErrorInfo label={errorMsg} />

        <Box className={classes.buttonContainer}>
          <Button
            id="button-uploadSupportingDocuments-back"
            className={classes.backButton}
            variant="outlined"
            onClick={() => goBackClick(isGroup, amendment, history)}
          >
            {t('components->>CompleteProfileDetails.Back')}
          </Button>

          <ApiButton
            id="button-uploadSupportingDocuments"
            className={classes.submitButton}
            status={apiButtonStatus}
            type="submit"
            onClick={uploadFileClickHandler}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.Review Application')}
          </ApiButton>
          {isGroup && applicationData && (
            <DraftTimeStamp savedAt={applicationData.lastUpdatedTime} />
          )}
          {!isGroup && <DraftMessage />}
        </Box>
      </ContentTemplate>
    </>
  );
};
export default UploadSupportingDocumentsContent;
